import { useState, useCallback, FocusEvent, ChangeEvent } from 'react';
import { isPasswordValid, validatePassword, type PasswordValidation } from '@flexo/general';

export interface ValidationRule {
  validate?: (value: string, allValues?: Record<string, string>) => boolean;
  errorMessage?: string;
}

export interface FormValidationConfig {
  fields: string[];
  validationRules?: Record<string, ValidationRule>;
  hasPasswordFields?: boolean;
}

export interface UseFormValidationReturn {
  errors: Record<string, boolean>;
  blur: Record<string, boolean>;
  passwordBlur: boolean;
  passwordValidation: PasswordValidation;
  handleBlur: (event: FocusEvent<HTMLInputElement>) => void;
  handleFocus: (event: FocusEvent<HTMLInputElement>) => void;
  handlePasswordValidation: (password: string, confirmPassword?: string) => void;
  setPasswordBlur: (value: boolean) => void;
}

/**
 * Custom hook for form validation with support for password fields
 * Handles blur states, error states, and password-specific validation
 * 
 * @param config - Configuration object with fields and validation rules
 * @param formValues - Current form values for cross-field validation
 * @returns Validation state and handlers
 */
export function useFormValidation(
  config: FormValidationConfig,
  formValues: Record<string, string>
): UseFormValidationReturn {
  const { fields, validationRules = {}, hasPasswordFields = false } = config;

  // Initialize states
  const initialErrorState = fields.reduce((acc, field) => ({ ...acc, [field]: false }), {});
  const initialBlurState = fields.reduce((acc, field) => ({ ...acc, [field]: false }), {});

  const [errors, setErrors] = useState<Record<string, boolean>>(initialErrorState);
  const [blur, setBlur] = useState<Record<string, boolean>>(initialBlurState);
  const [passwordBlur, setPasswordBlur] = useState(false);
  const [passwordValidation, setPasswordValidation] = useState<PasswordValidation>({
    lengthValid: false,
    hasNumber: false,
    hasSpecialChar: false,
    hasUpperCase: false,
    hasLowerCase: false,
    passwordsMatch: false,
  });

  /**
   * Validate password and update validation state
   */
  const handlePasswordValidation = useCallback(
    (password: string, confirmPassword?: string) => {
      if (!password) return;
      const validation = validatePassword(password, confirmPassword || '');
      setPasswordValidation(validation);
    },
    []
  );

  /**
   * Handle blur event for form fields
   * Only validates and shows errors if field has content
   */
  const handleBlur = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      const { name, value } = event.target;
      const fieldName = name;

      // Only validate if field has content
      if (value.length > 0) {
        // Special handling for password fields
        if (fieldName === 'password' || fieldName === 'newPassword') {
          setPasswordBlur(true);
          setErrors((prev) => ({
            ...prev,
            [fieldName]: !isPasswordValid(value),
          }));
          setBlur((prev) => ({ ...prev, [fieldName]: true }));
          return;
        }

        // Special handling for confirm password
        if (fieldName === 'confirmPassword') {
          const passwordField = formValues.password || formValues.newPassword || '';
          setErrors((prev) => ({
            ...prev,
            [fieldName]: passwordField !== value,
          }));
          setBlur((prev) => ({ ...prev, [fieldName]: true }));
          return;
        }

        // Custom validation rule
        if (validationRules[fieldName]?.validate) {
          setErrors((prev) => ({
            ...prev,
            [fieldName]: !validationRules[fieldName].validate?.(value, formValues),
          }));
          setBlur((prev) => ({ ...prev, [fieldName]: true }));
          return;
        }

        // Default: just mark as blurred
        setBlur((prev) => ({ ...prev, [fieldName]: true }));
      }
    },
    [formValues, validationRules]
  );

  /**
   * Handle focus event for form fields
   * Clears errors and blur state for better UX
   */
  const handleFocus = useCallback((event: FocusEvent<HTMLInputElement>) => {
    const { name } = event.target;
    const fieldName = name;

    // Clear error state
    setErrors((prev) => ({ ...prev, [fieldName]: false }));

    // Special handling for password fields
    if (fieldName === 'password' || fieldName === 'newPassword') {
      setPasswordBlur(false);
    }
  }, []);

  return {
    errors,
    blur,
    passwordBlur,
    passwordValidation,
    handleBlur,
    handleFocus,
    handlePasswordValidation,
    setPasswordBlur,
  };
}

export default useFormValidation;

